/**
 * TerminationLetterRow (P6-S3) - the Dankesbrief's state on the termination row in «Mandate &
 * Eintragungen» (Stefan 7.9, 19.1): «Bestätigung versandt per Portal am …», «Bestätigung ausstehend,
 * Brief drucken», «Bestätigung fehlgeschlagen», with «Brief drucken» + «gedruckt und versendet» for the
 * post channel and «Erneut senden» for a failed letter.
 *
 * #103 review: a letter failed with versand_unklar (the mail may have gone) reads «Versand unklar» with
 * a «Postausgang prüfen» hint, offers «Als versendet markieren» first, and «Erneut senden» only after an
 * explicit confirm (the api refuses it without one). A 409 on an action refetches the row and shows the
 * server's message.
 *
 * The buttons need what the api needs (review S3-4): the route key communication.send and the in-code
 * key mandate.eingangskontrolle; the print copy document.download.
 */

import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatZurichDateDe } from '@brokly/shared';
import type { TerminationDto, TerminationLetterDto } from '@brokly/shared';
import { Button } from '../../../components/ui/Button';
import { useEffectivePermissions } from '../../administration/useEffectivePermissions';
import { ApiError, apiClient } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';

export const POLL_MS = 10_000;
export const POLL_MAX_MS = 120_000;

/** Review M5: 10 s while the letter is in flight, never longer than 2 minutes after the row was shown. */
export function letterPollInterval(letter: TerminationLetterDto | undefined, shownAt: number, now: number): number | false {
  const inFlight = !!letter && ((letter.status === 'ausstehend' && letter.channel === null) || letter.status === 'versandt');
  return inFlight && now - shownAt < POLL_MAX_MS ? POLL_MS : false;
}

const CHANNEL_LABEL: Record<string, string> = { portal: 'Portal', email: 'E-Mail', post: 'Briefpost', anderer: 'anderem Weg' };
const MANUAL_CHANNELS: Array<{ value: 'post' | 'email' | 'portal' | 'anderer'; label: string }> = [
  { value: 'post', label: 'Post' },
  { value: 'email', label: 'E-Mail' },
  { value: 'portal', label: 'Portal' },
  { value: 'anderer', label: 'anderer Weg' },
];

/** The letter state in words. */
export function letterStateText(letter: TerminationLetterDto | undefined): string | null {
  if (!letter || letter.status === null) return null;
  const via = letter.channel ? ` per ${CHANNEL_LABEL[letter.channel] ?? letter.channel}` : '';
  switch (letter.status) {
    case 'abgeschlossen':
      return `Bestätigung versandt${via}${letter.sent_at ? ` am ${formatZurichDateDe(letter.sent_at) ?? '-'}` : ''}`;
    case 'versandt':
      return `Bestätigung wird versendet${via}`;
    case 'fehlgeschlagen':
      return letter.failure_reason === 'versand_unklar' ? 'Bestätigung: Versand unklar' : 'Bestätigung fehlgeschlagen';
    case 'storniert':
      return 'Bestätigung storniert (Widerruf)';
    default:
      return letter.channel === 'post' ? 'Bestätigung ausstehend, Brief drucken' : 'Bestätigung ausstehend';
  }
}

/**
 * `readOnly` (an inaktiv customer): «gedruckt und versendet» and «Als versendet markieren» stay (they record a
 * letter that already went out); «Erneut senden» and «Als Brief versenden» are Kundenkommunikation, stopped.
 */
export function TerminationLetterRow({ terminationId, readOnly = false }: { terminationId: string; readOnly?: boolean }) {
  const qc = useQueryClient();
  const { can } = useEffectivePermissions();
  const canAct = can('communication.send') && can('mandate.eingangskontrolle');
  const canDownload = can('document.download');
  const [error, setError] = useState<string | null>(null);
  const mountedAt = useRef(Date.now());
  const [remarkOpen, setRemarkOpen] = useState(false);
  const [remark, setRemark] = useState('');
  const [manualChannel, setManualChannel] = useState<'post' | 'email' | 'portal' | 'anderer' | ''>('');
  const [confirmResend, setConfirmResend] = useState(false);

  const q = useQuery({
    queryKey: qk.terminationById(terminationId),
    queryFn: () => apiClient.mandateTermination.get(terminationId),
    // The job sends the letter after the Freigabe: poll every 10 s while it is in flight (no channel
    // yet, or versandt), for at most 2 minutes after the row was shown (review M5).
    refetchInterval: (query) => letterPollInterval(query.state.data?.letter, mountedAt.current, Date.now()),
  });
  const done = (t: TerminationDto) => {
    // Round 2 M4: an action puts the letter back in flight: the bounded polling starts again.
    mountedAt.current = Date.now();
    qc.setQueryData(qk.terminationById(terminationId), t);
    void qc.invalidateQueries({ queryKey: ['mandate', 'termination-letters'] });
  };
  /** #103 review: a 409 means the row moved on (another user, the job, a Widerruf): refetch it, say why. */
  const failed409 = (fallback: string) => (err: unknown) => {
    if (err instanceof ApiError && err.status === 409) {
      setError(err.message || fallback);
      void qc.invalidateQueries({ queryKey: qk.terminationById(terminationId) });
      void qc.invalidateQueries({ queryKey: ['mandate', 'termination-letters'] });
      return;
    }
    setError(fallback);
  };
  const printed = useMutation({
    mutationFn: () => apiClient.mandateTermination.letterPrinted(terminationId),
    onMutate: () => setError(null),
    onSuccess: done,
    onError: failed409('Die Bestätigung konnte nicht als versendet erfasst werden.'),
  });
  const resend = useMutation({
    mutationFn: (confirmUnclear: boolean) =>
      confirmUnclear
        ? apiClient.mandateTermination.letterResend(terminationId, { confirmUnclear: true })
        : apiClient.mandateTermination.letterResend(terminationId),
    onMutate: () => setError(null),
    onSuccess: (t: TerminationDto) => {
      setConfirmResend(false);
      done(t);
    },
    onError: failed409('Die Bestätigung konnte nicht erneut gesendet werden.'),
  });
  const toPost = useMutation({
    mutationFn: () => apiClient.mandateTermination.letterToPost(terminationId),
    onMutate: () => setError(null),
    onSuccess: done,
    onError: failed409('Die Bestätigung konnte nicht auf Briefpost umgestellt werden.'),
  });
  const markSent = useMutation({
    mutationFn: () => apiClient.mandateTermination.letterMarkSent(terminationId, remark.trim(), manualChannel as 'post'),
    onMutate: () => setError(null),
    onSuccess: (t: TerminationDto) => {
      setRemarkOpen(false);
      setRemark('');
      done(t);
    },
    onError: failed409('Die Bestätigung konnte nicht als versendet markiert werden.'),
  });
  const openBrief = () => {
    setError(null);
    apiClient.mandateTermination
      .letterBlob(terminationId)
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank', 'noopener');
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      })
      .catch(() => setError('Der Brief konnte nicht geöffnet werden.'));
  };

  const letter = q.data?.letter;
  const text = letterStateText(letter);
  if (text === null) return null;
  const postPending = letter!.status === 'ausstehend' && letter!.channel === 'post';
  const failed = letter!.status === 'fehlgeschlagen';
  // The mail may already be with the customer: on a failed letter, and on one the Widerruf cancelled.
  const unclear = letter!.failure_reason === 'versand_unklar' && (failed || letter!.status === 'storniert');
  const resendButton = readOnly ? null : (
    <Button
      size="sm"
      variant="outline"
      disabled={resend.isPending}
      onClick={() => (unclear && failed ? setConfirmResend(true) : resend.mutate(false))}
    >
      Erneut senden
    </Button>
  );
  const remarkButton = (
    <Button size="sm" variant="outline" onClick={() => setRemarkOpen((v) => !v)}>
      Als versendet markieren
    </Button>
  );
  const postButton = letter!.document_id && !readOnly ? (
    <Button size="sm" variant="outline" disabled={toPost.isPending} onClick={() => toPost.mutate()}>
      Als Brief versenden
    </Button>
  ) : null;
  return (
    <div className="flex w-full flex-wrap items-center gap-2 text-xs" data-testid="me-termination-letter">
      <span className={failed ? 'text-destructive' : 'text-muted-foreground'}>{text}</span>
      {unclear && (
        <span className="text-muted-foreground" data-testid="letter-unclear-hint">
          Postausgang prüfen: die E-Mail ist möglicherweise schon beim Kunden.
        </span>
      )}
      {letter!.remark && (
        <span className="text-muted-foreground" data-testid="letter-remark">
          Vermerk: {letter!.remark}
        </span>
      )}
      {postPending && canDownload && (
        <Button size="sm" variant="outline" onClick={openBrief}>
          Brief drucken
        </Button>
      )}
      {postPending && canAct && (
        <Button size="sm" variant="outline" disabled={printed.isPending} onClick={() => printed.mutate()}>
          gedruckt und versendet
        </Button>
      )}
      {failed && canAct && unclear && (
        <>
          {remarkButton}
          {postButton}
          {resendButton}
        </>
      )}
      {failed && canAct && !unclear && (
        <>
          {resendButton}
          {postButton}
          {remarkButton}
        </>
      )}
      {failed && canAct && unclear && confirmResend && !readOnly && (
        <span className="flex w-full items-center gap-2" data-testid="letter-resend-confirm">
          <span className="text-destructive">
            Postausgang geprüft und die E-Mail nicht gefunden? Sonst erhält der Kunde die Bestätigung zweimal.
          </span>
          <Button size="sm" disabled={resend.isPending} onClick={() => resend.mutate(true)}>
            Trotzdem erneut senden
          </Button>
          <Button size="sm" variant="outline" onClick={() => setConfirmResend(false)}>
            Abbrechen
          </Button>
        </span>
      )}
      {failed && canAct && remarkOpen && (
        <span className="flex w-full items-center gap-2">
          <select
            aria-label="Versendet per"
            className="rounded border px-2 py-1 text-xs"
            value={manualChannel}
            onChange={(e) => setManualChannel(e.target.value as typeof manualChannel)}
          >
            <option value="">Versendet per …</option>
            {MANUAL_CHANNELS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
          <input
            aria-label="Vermerk zum Versand"
            className="min-w-0 flex-1 rounded border px-2 py-1 text-xs"
            maxLength={2000}
            placeholder="Wie und wann wurde die Bestätigung versendet?"
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
          />
          <Button size="sm" disabled={remark.trim() === '' || manualChannel === '' || markSent.isPending} onClick={() => markSent.mutate()}>
            Speichern
          </Button>
        </span>
      )}
      {error && (
        <span className="text-destructive" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
